import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { formatCents, formatMesRef } from "@/lib/format";
import {
  ROTULO_BLOQUEIO,
  type BloqueioFechamento,
  type CodigoBloqueio,
  type FolhaTotais,
  type PendenciaCadastro,
} from "@/data/folha";
import { IgnorarMotivoDialog } from "@/components/folha/IgnorarMotivoDialog";
import { toast } from "sonner";
import { AlertTriangle, EyeOff, Loader2, UserX, Wrench } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  competencia: string;
  totais: FolhaTotais;
  colaboradores: number;
  bloqueios: BloqueioFechamento[];
  /** Cadastro incompleto: não bloqueia, mas a folha fecha "com pendência". */
  avisos: PendenciaCadastro[];
  onFechada: () => void;
  /** "Corrigir agora": fecha o diálogo e leva o RH para onde se resolve o bloqueio. */
  onCorrigir: (codigo: CodigoBloqueio) => void;
  /** Depois de ignorar um bloqueio: recarrega a folha (e com ela os bloqueios). */
  onIgnorado: () => Promise<void>;
}

/** O que acontece ao ignorar cada bloqueio — vai no modal do motivo. */
const EFEITO_IGNORAR: Record<CodigoBloqueio, string> = {
  PENDENCIAS_IMPORTACAO:
    "Os valores dessas linhas não entram na folha de ninguém. Use para quem já foi desligado e ainda veio na planilha.",
  LIQUIDO_NAO_POSITIVO:
    "A folha fecha com esses colaboradores com líquido zerado ou negativo; eles ficam de fora do envio ao ERP.",
  ITEM_SEM_TOMADOR:
    "Esses colaboradores recebem o aviso de emissão sem a empresa tomadora indicada.",
  SEM_COPARTICIPACAO:
    "A competência fecha sem descontar a coparticipação da Amil Saúde de ninguém.",
  HOLERITE_DIVERGENTE:
    "Os holerites com pendência são publicados como estão para os colaboradores CLT.",
};

export const FecharFolhaDialog = ({
  open,
  onClose,
  folhaId,
  competencia,
  totais,
  colaboradores,
  bloqueios,
  avisos,
  onFechada,
  onCorrigir,
  onIgnorado,
}: Props) => {
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [ignorando, setIgnorando] = useState<BloqueioFechamento | null>(null);

  const bloqueado = bloqueios.length > 0;

  const fechar = async () => {
    setSalvando(true);
    try {
      await api.post(`/folhas/${folhaId}/fechar`, {
        ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
      });
      toast.success(
        avisos.length > 0
          ? `Folha de ${formatMesRef(competencia)} fechada com pendência`
          : `Folha de ${formatMesRef(competencia)} fechada`,
        { description: "Quando a folha estiver final, use \"Enviar para o ERP\"." },
      );
      onFechada();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao fechar a folha");
    } finally {
      setSalvando(false);
    }
  };

  const ignorar = async (motivo: string) => {
    if (!ignorando) return;
    await api.post(`/folhas/${folhaId}/bloqueios/ignorar`, { codigo: ignorando.codigo, motivo });
    toast.success(`${ROTULO_BLOQUEIO[ignorando.codigo]}: ignorado`);
    await onIgnorado();
  };

  return (
    <>
      <AlertDialog open={open && !ignorando} onOpenChange={(v) => !v && onClose()}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bloqueado ? "Não é possível fechar ainda" : `Fechar ${formatMesRef(competencia)}`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="max-h-[65vh] space-y-3 overflow-y-auto">
                {bloqueado ? (
                  <>
                    <p>Corrija ou ignore (com motivo) cada ponto abaixo para fechar a competência:</p>
                    {bloqueios.map((b) => (
                      <div
                        key={b.codigo}
                        className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
                      >
                        <div className="flex items-start gap-1.5 text-sm font-medium text-destructive">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          {b.mensagem}
                        </div>
                        {b.colaboradores.length > 0 && (
                          <ul className="list-disc pl-6 text-xs">
                            {b.colaboradores.slice(0, 8).map((c, i) => (
                              <li key={`${c.id}-${i}`}>
                                {c.nome}
                                {c.detalhe && <span className="text-muted-foreground"> — {c.detalhe}</span>}
                              </li>
                            ))}
                            {b.colaboradores.length > 8 && (
                              <li className="text-muted-foreground">
                                e mais {b.colaboradores.length - 8}
                              </li>
                            )}
                          </ul>
                        )}
                        <div className="flex flex-wrap gap-2 pt-0.5">
                          <Button
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                            onClick={() => {
                              onClose();
                              onCorrigir(b.codigo);
                            }}
                          >
                            <Wrench className="h-3.5 w-3.5" />
                            Corrigir agora
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-xs"
                            onClick={() => setIgnorando(b)}
                          >
                            <EyeOff className="h-3.5 w-3.5" />
                            Ignorar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <p>
                      Ao fechar, <strong>{colaboradores} colaborador(es)</strong> passam a ver o aviso
                      para emitir a nota fiscal, somando{" "}
                      <strong>{formatCents(totais.liquido)}</strong>.
                    </p>
                    <p>
                      O lançamento no ERP <strong>não é feito agora</strong>: depois de fechar (e de
                      reabrir para as comissões, se for o caso), use o botão{" "}
                      <strong>Enviar para o ERP</strong>.
                    </p>
                    <p>
                      As parcelas de empréstimo desta competência serão baixadas. Reabrir depois é
                      possível, mas quem já tiver emitido NF pode ficar com valor divergente.
                    </p>
                    {avisos.length > 0 && (
                      <div className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-amber-800 dark:text-amber-300">
                        <div className="flex items-start gap-1.5 text-sm font-medium">
                          <UserX className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          A folha vai fechar com pendência: {avisos.length} colaborador(es) com
                          cadastro incompleto
                        </div>
                        <ul className="list-disc pl-6 text-xs">
                          {avisos.slice(0, 8).map((a) => (
                            <li key={a.id}>
                              {a.nome} — {a.detalhe}
                            </li>
                          ))}
                          {avisos.length > 8 && <li>e mais {avisos.length - 8}</li>}
                        </ul>
                        <p className="text-xs">
                          Eles só conseguem enviar a NF depois de completar o cadastro, e ficam de
                          fora do envio ao ERP até lá.
                        </p>
                      </div>
                    )}
                    <div className="space-y-1.5 pt-1">
                      <Label htmlFor="obs-fechamento">Observação (opcional)</Label>
                      <Textarea
                        id="obs-fechamento"
                        value={observacao}
                        onChange={(e) => setObservacao(e.target.value)}
                        maxLength={500}
                        rows={2}
                        placeholder="Ex: comissões de março conferidas com o comercial"
                      />
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>
              {bloqueado ? "Fechar" : "Cancelar"}
            </AlertDialogCancel>
            {!bloqueado && (
              <Button onClick={() => void fechar()} disabled={salvando}>
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {avisos.length > 0 ? "Fechar com pendência" : "Fechar competência"}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IgnorarMotivoDialog
        open={!!ignorando}
        onClose={() => setIgnorando(null)}
        titulo={ignorando ? `Ignorar: ${ROTULO_BLOQUEIO[ignorando.codigo]}` : "Ignorar"}
        descricao={
          ignorando && (
            <>
              <p>{EFEITO_IGNORAR[ignorando.codigo]}</p>
              {ignorando.colaboradores.length > 0 && (
                <p>
                  Vale para {ignorando.colaboradores.length}{" "}
                  {ignorando.codigo === "PENDENCIAS_IMPORTACAO" ? "linha(s)" : "colaborador(es)"}{" "}
                  listados agora; se aparecer outro com o mesmo problema, ele volta a bloquear.
                </p>
              )}
            </>
          )
        }
        onConfirmar={ignorar}
      />
    </>
  );
};
